import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { toDateInputValue } from '../lib/format';
import { createExperiment, updateExperiment } from '../services/experiments';
import type { Experiment } from '../services/experiments';
import { Button, Input, Modal, Textarea } from './ui';

/** Start an experiment, or correct the title, date or aim of one. */
export default function ExperimentDialog({
  projectId,
  target,
  onClose,
}: {
  projectId: string;
  target: Experiment | 'new' | null;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isNew = target === 'new';
  const [title, setTitle] = useState('');
  const [date, setDate] = useState('');
  const [aim, setAim] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!target) return;
    setTitle(isNew ? '' : target.title);
    setDate(isNew ? toDateInputValue(new Date()) : target.date);
    setAim(isNew ? '' : target.aim);
    setError(null);
  }, [target, isNew]);

  async function save() {
    if (!user || !target) return;
    if (!title.trim()) return setError('Give the experiment a title.');
    if (!date) return setError('Choose the date.');
    setSaving(true);
    setError(null);
    try {
      if (isNew) {
        const id = await createExperiment(projectId, { title, date, aim }, {
          uid: user.uid,
          name: user.displayName ?? user.email ?? null,
        });
        onClose();
        navigate(`/p/${projectId}/e/${id}`);
      } else {
        await updateExperiment(projectId, target.id, { title, date, aim });
        onClose();
      }
    } catch (e) {
      setError((e as Error)?.message ?? 'Could not save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={target !== null} onClose={onClose} title={isNew ? 'New experiment' : 'Edit experiment'}>
      <div className="space-y-3">
        <label className="block">
          <span className="field-label">Title</span>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder=""
            autoFocus
          />
        </label>
        <label className="block">
          <span className="field-label">Date</span>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </label>
        <label className="block">
          <span className="field-label">Aim (optional)</span>
          <Textarea
            rows={3}
            value={aim}
            onChange={(e) => setAim(e.target.value)}
            placeholder="What this experiment is meant to find out"
          />
        </label>
        {error && <p className="text-sm text-red-300">{error}</p>}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={saving} onClick={() => void save()}>
            {isNew ? 'Start experiment' : 'Save'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
